import React, { useEffect } from 'react';
import { useRegisterRequestStore } from '@/store/registerRequestStore';
import { RegisterRequestList } from './register-requests/RegisterRequestList';
import { RegisterRequestDialog } from './register-requests/RegisterRequestDialog';
import { EditRequestDialog } from './register-requests/EditRequestDialog';
import { RejectRequestDialog } from './register-requests/RejectRequestDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { RegisterRequest } from '@/types/register';

export function RegisterRequests() {
  const {
    requests,
    isLoading,
    error,
    hasInitialFetch,
    fetchRequests,
    reset
  } = useRegisterRequestStore();

  const [editingRequest, setEditingRequest] = React.useState<RegisterRequest | null>(null);
  const [approvingRequest, setApprovingRequest] = React.useState<RegisterRequest | null>(null);
  const [rejectingRequest, setRejectingRequest] = React.useState<RegisterRequest | null>(null);
  const [selectedRole, setSelectedRole] = React.useState<'user' | 'admin'>('user');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!hasInitialFetch && mounted) {
        await fetchRequests();
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [hasInitialFetch]);

  // Filter pending requests
  const pendingRequests = requests.filter(r => r.status === 'pending');

  if (isLoading && !hasInitialFetch) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow-sm">
        <p className="text-red-500">{error}</p>
        <Button 
          onClick={() => {
            reset();
            fetchRequests();
          }}
          className="mt-4"
          variant="outline"
        >
          إعادة المحاولة
        </Button>
      </div>
    );
  }

  if (pendingRequests.length === 0) {
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow-sm">
        <p className="text-gray-500">لا توجد طلبات تسجيل معلقة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">طلبات التسجيل</h2>

      <RegisterRequestList
        requests={pendingRequests}
        onEdit={setEditingRequest}
        onApprove={setApprovingRequest}
        onReject={setRejectingRequest}
      />

      <RegisterRequestDialog
        request={approvingRequest}
        selectedRole={selectedRole}
        onRoleChange={setSelectedRole}
        onClose={() => setApprovingRequest(null)}
      />

      <EditRequestDialog
        request={editingRequest}
        onClose={() => setEditingRequest(null)}
      />

      <RejectRequestDialog
        requestId={rejectingRequest?.id || null}
        requestName={rejectingRequest?.name || ''}
        onClose={() => setRejectingRequest(null)}
      />
    </div>
  );
}